import { type JSX, useEffect, useMemo, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { GenerativeAiDisclaimer } from 'Components/ArtificialIntelligence/GenerativeAiDisclaimer';
import { Button } from 'Components/Buttons/Buttons';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { CONTACT_INFORMATION_FOR_ERROR_MESSAGE, GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_ADD_CREATE, ICON_DELETE_REMOVE } from 'Config/Icons';
import { getFrameworkGroupControlParts } from 'Helpers/ControlFormatter/ControlFormatter';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { AiOperationalControlAssessment, AiOperationalControlAssessmentGenerationStatus } from 'Models/ArtificialIntelligence';
import { ComplianceRequirementForControlResponse } from 'Models/ComplianceRequirements';
import { OperationalControl } from 'Models/OperationalControls';

import styles from './ArtificialIntelligenceControlAssessment.module.css';
import { ArtificialIntelligenceControlAssessmentResults } from './ArtificialIntelligenceControlAssessmentResults';

enum DisplayedModal {
    CLEAR,
    GENERATE,
}

export interface ArtificialIntelligenceControlAssessmentProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    complianceRequirements: ComplianceRequirementForControlResponse[];
    control: OperationalControl;
    disabled: boolean;
}

export const ArtificialIntelligenceControlAssessment = (props: ArtificialIntelligenceControlAssessmentProps) => {
    const [displayedModal, setDisplayedModal] = useState<DisplayedModal>();
    const [assessment, setAssessment] = useState<AiOperationalControlAssessment>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    const { controlFramework, controlGroupId, controlId } = useMemo(() => {
        return getFrameworkGroupControlParts(props.control);
    }, [props.control]);

    useEffect(() => {
        const getAssessment = async (): Promise<void> => {
            try {
                const response = await props.artificialIntelligenceApi.aiOperationalControlsGet(controlFramework, controlGroupId, controlId);
                setAssessment(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        getAssessment();
    }, [props.artificialIntelligenceApi, controlFramework, controlGroupId, controlId]);

    if (loadingErrorOccurred) {
        return (
            <>
                <Text variant="Header2">SummIT Guide™ Control Assessment</Text>
                <hr />
                <Text>{GENERIC_ERROR_MESSAGE}</Text>
            </>
        );
    } else if (!assessment) {
        return <Placeholder />;
    }

    return (
        <>
            {displayedModal === DisplayedModal.CLEAR && (
                <ConfirmationModal
                    operationType={ConfirmationModalOperationType.DESTRUCTIVE}
                    headerText="Clear Assessment"
                    buttonText="Clear"
                    buttonIcon={ICON_DELETE_REMOVE}
                    buttonLoadingText="Clearing..."
                    areYouSureText="Are you sure you want to clear the SummIT Guide™ control assessment?"
                    hideModal={() => setDisplayedModal(undefined)}
                    performOperation={async () => {
                        await props.artificialIntelligenceApi.aiOperationalControlsDelete(controlFramework, controlGroupId, controlId);
                        setAssessment({});
                        return 'Assessment cleared.';
                    }}
                >
                    <Text>This action cannot be undone. A new SummIT Guide™ control assessment can be generated later if desired.</Text>
                </ConfirmationModal>
            )}
            {displayedModal === DisplayedModal.GENERATE && (
                <ConfirmationModal
                    operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE}
                    headerText="Generate SummIT Guide™ Control Assessment"
                    buttonText="Generate"
                    buttonIcon={ICON_ADD_CREATE}
                    buttonLoadingText="Generating..."
                    areYouSureText="Are you sure you want to generate a SummIT Guide™ control assessment?"
                    hideModal={() => setDisplayedModal(undefined)}
                    performOperation={async () => {
                        await props.artificialIntelligenceApi.aiOperationalControlsCreate(controlFramework, controlGroupId, controlId);
                        setAssessment({ generation_status: AiOperationalControlAssessmentGenerationStatus.IN_PROGRESS });
                        return 'Control assessment generation started. Refresh the page in a few minutes to see the assessment.';
                    }}
                >
                    <div className={styles.disclaimer}>
                        <GenerativeAiDisclaimer />
                    </div>
                </ConfirmationModal>
            )}

            <div className={styles.aiHeader}>
                <div className={styles.aiHeaderText}>
                    <Text variant="Header2">SummIT Guide™ Control Assessment</Text>
                    {assessment.generation_status === AiOperationalControlAssessmentGenerationStatus.SUCCESS && assessment.final_result?.timestamp && <Text noStyles>{`Generated: ${iso8601ToUsDateShort(assessment.final_result.timestamp)}`}</Text>}
                </div>
                <div className={styles.aiHeaderButtons}>
                    {assessment.final_result !== undefined && (
                        <Button variant="secondary" disabled={props.disabled || assessment.generation_status === AiOperationalControlAssessmentGenerationStatus.IN_PROGRESS} onClick={() => setDisplayedModal(DisplayedModal.CLEAR)} isLoading={false} loadingText="Clearing..." fontAwesomeImage={ICON_DELETE_REMOVE}>
                            Clear
                        </Button>
                    )}
                    <Button variant="secondary" disabled={props.disabled || assessment.generation_status === AiOperationalControlAssessmentGenerationStatus.IN_PROGRESS} onClick={() => setDisplayedModal(DisplayedModal.GENERATE)} isLoading={false} loadingText="Generating..." fontAwesomeImage={ICON_ADD_CREATE}>
                        Generate New
                    </Button>
                </div>
            </div>
            <hr />
            {((): JSX.Element => {
                if (assessment.generation_status === undefined) {
                    return <Text>A SummIT Guide™ control assessment has not been generated.</Text>;
                }

                switch (assessment.generation_status) {
                    case AiOperationalControlAssessmentGenerationStatus.IN_PROGRESS:
                        return <Text>The assessment is being generated. Refresh the page in a few minutes to see the assessment.</Text>;
                    case AiOperationalControlAssessmentGenerationStatus.FAILURE:
                        return <Text>{`The assessment failed to complete. ${CONTACT_INFORMATION_FOR_ERROR_MESSAGE}`}</Text>;
                    case AiOperationalControlAssessmentGenerationStatus.SUCCESS:
                        return <ArtificialIntelligenceControlAssessmentResults complianceRequirements={props.complianceRequirements} control={props.control} finalResult={assessment.final_result!} />;
                }
            })()}
        </>
    );
};
